import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomDynamicRepeaterConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';

export const defaultCustomDynamicRepeaterConfigFormModel: ICustomDynamicRepeaterConfigurationFormModel =
  {
    ...fieldsBaseConfigurationFormModel(),
    dynamicRepeaterSection: {
      itemIdField: 'id',
      itemLabelField: 'label',
      emptyStateMessage:
        'No items available. Items will be populated automatically from the data source.',
      enableDebugLogging: false,
    },
  };

export const customDynamicRepeaterFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultCustomDynamicRepeaterConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        {
          key: 'dynamicRepeaterSection',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Dynamic Repeater Configuration',
            description: 'Configure how API data is mapped to repeater items',
          },
          fieldGroup: [
            {
              key: 'itemIdField',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Item ID Field',
                placeholder: 'id',
                required: true,
                description:
                  'Field name in API data for item ID (supports dot notation, e.g., "user.id")',
              },
            },
            {
              key: 'itemLabelField',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Item Label Field',
                placeholder: 'label',
                required: true,
                description:
                  'Field name in API data for item display text (supports dot notation, e.g., "user.name")',
              },
            },
            {
              key: 'emptyStateMessage',
              type: EIremboFormlyFieldTypes.textarea,
              props: {
                label: 'Empty State Message',
                placeholder: 'No items available',
                rows: 2,
                description: 'Message shown when no items are available',
              },
            },
            {
              key: 'enableDebugLogging',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Enable Debug Logging',
                formCheck: 'switch',
                description:
                  'Enable console logging for development and debugging',
              },
            },
          ],
        },
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const customDynamicRepeaterFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ICustomDynamicRepeaterConfigurationFormModel => {
  const baseModel = baseFieldParseIntoSettingsFormModel(field);

  // Extract dynamic repeater config from field props
  const dynamicRepeaterConfig = field.props?.['dynamicRepeaterConfig'] || {};

  return {
    ...baseModel,
    dynamicRepeaterSection: {
      itemIdField: dynamicRepeaterConfig.itemIdField || 'id',
      itemLabelField: dynamicRepeaterConfig.itemLabelField || 'label',
      emptyStateMessage:
        dynamicRepeaterConfig.emptyStateMessage ||
        'No items available. Items will be populated automatically from the data source.',
      enableDebugLogging: dynamicRepeaterConfig.enableDebugLogging || false,
    },
  };
};

export const customDynamicRepeaterFieldUpdateFromSettingsFormModel = (
  model: ICustomDynamicRepeaterConfigurationFormModel,
  inputField: FormlyFieldConfig
): FormlyFieldConfig => {
  // Update base field properties
  const field = baseFieldUpdateFromSettingsFormModel(model, inputField);

  // Ensure props exists
  field.props = field.props || {};

  // Set dynamic repeater configuration
  field.props['dynamicRepeaterConfig'] = {
    itemIdField: model.dynamicRepeaterSection.itemIdField,
    itemLabelField: model.dynamicRepeaterSection.itemLabelField,
    emptyStateMessage: model.dynamicRepeaterSection.emptyStateMessage,
    enableDebugLogging: model.dynamicRepeaterSection.enableDebugLogging,
  };

  return field;
};

export const customDynamicRepeaterFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};
  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  return model;
};

export const customDynamicRepeaterFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  inputField: FormlyFieldConfig
): FormlyFieldConfig => {
  return setRequiredValidatorUpdateFieldFromModel(model, inputField);
};

export const customDynamicRepeaterFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const customDynamicRepeaterFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};
